// File: frontend/src/components/CaseManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder, Users, Clock, ChevronDown, ChevronRight, Plus, Search,
  Edit2, Trash2, User, UserPlus, X, Save, FileText,
  Building2, Link as LinkIcon
} from 'lucide-react';
import { casesAPI, peopleAPI } from '../utils/api';
import ReportGenerator from './ReportGenerator';
import { useData } from '../contexts/DataContext';
import { useUI } from '../contexts/UIContext';

// ── Entity Picker Modal ───────────────────────────────────────────────────────
const EntityPickerModal = ({ title, items, selectedIds, onToggle, onConfirm, onCancel, renderItem }) => {
  const [search, setSearch] = useState('');
  const filtered = items.filter(item => renderItem(item).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full pl-9 pr-3 py-2 border dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100" />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1 border dark:border-gray-700 rounded-lg">
            {filtered.map(item => (
              <label key={item.id} className={`flex items-center space-x-3 px-3 py-2 cursor-pointer transition-colors ${selectedIds.includes(item.id) ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
                <input type="checkbox" checked={selectedIds.includes(item.id)}
                  onChange={() => onToggle(item.id)}
                  className="h-4 w-4 text-blue-600 rounded" />
                <span className="text-sm text-gray-900 dark:text-slate-100">{renderItem(item)}</span>
              </label>
            ))}
            {filtered.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No results</p>}
          </div>
        </div>
        <div className="flex justify-end space-x-2 px-5 pb-5">
          <button onClick={onCancel} className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
          <button onClick={() => onConfirm(selectedIds)} disabled={selectedIds.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50">
            Add {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CaseManagement = () => {
  const { people, fetchPeople, businesses, fetchBusinesses, fetchCases: fetchCasesContext } = useData();
  const { setEditingPerson, setSelectedPersonForDetail, setEditingBusiness } = useUI();

  const [cases, setCases] = useState([]);
  const [expandedCases, setExpandedCases] = useState({});
  const [editingCase, setEditingCase] = useState(null);
  const [showNewCaseForm, setShowNewCaseForm] = useState(false);
  const [newCaseData, setNewCaseData] = useState({ case_name: '', description: '', status: 'active' });
  const [searchTerm, setSearchTerm] = useState('');

  // Quick-add new person
  const [showQuickAdd, setShowQuickAdd] = useState(null); // caseId
  const [quickAddPerson, setQuickAddPerson] = useState({ firstName: '', lastName: '' });

  // Entity picker state
  const [pickerMode, setPickerMode] = useState(null); // { caseId, type: 'people'|'businesses' }
  const [pickerSelected, setPickerSelected] = useState([]);

  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [reportCaseId, setReportCaseId] = useState(null);

  const fetchCases = useCallback(async () => {
    try {
      const data = await casesAPI.getAll();
      const processed = data.map(c => ({
        ...c,
        people:     c.people_data     || [],
        businesses: c.businesses_data || [],
        peopleCount:    (c.people_data     || []).length,
        businessCount:  (c.businesses_data || []).length,
        lastUpdate: new Date(Math.max(
          new Date(c.updated_at || c.created_at).getTime(),
          ...(c.people_data || []).map(p => new Date(p.updated_at || p.created_at || 0).getTime())
        )),
      }));
      setCases(processed.sort((a, b) => b.lastUpdate - a.lastUpdate));
    } catch (err) {
      console.error('Error fetching cases:', err);
    }
  }, []);

  useEffect(() => { fetchCases(); }, [fetchCases, people]);

  // Refresh all 3 core data sets after any mutation
  const refreshAll = useCallback(() => {
    fetchCases();
    fetchPeople();
    fetchBusinesses();
    fetchCasesContext();
  }, [fetchCases, fetchPeople, fetchBusinesses, fetchCasesContext]);

  const getTimeAgo = (date) => {
    const days = Math.floor((new Date() - date) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  const filteredCases = cases.filter(c =>
    c.case_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Case CRUD ────────────────────────────────────────────────────────────
  const handleCreateCase = async () => {
    if (!newCaseData.case_name.trim()) return;
    try {
      await casesAPI.create(newCaseData);
      setNewCaseData({ case_name: '', description: '', status: 'active' });
      setShowNewCaseForm(false);
      refreshAll();
    } catch { alert('Failed to create case'); }
  };

  const handleUpdateCase = async (caseId, updates) => {
    try { await casesAPI.update(caseId, updates); refreshAll(); setEditingCase(null); }
    catch { alert('Failed to update case'); }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm('Delete this case? People and businesses will remain but lose this association.')) return;
    try { await casesAPI.delete(caseId); refreshAll(); }
    catch { alert('Failed to delete case'); }
  };

  // ── Quick-add new person ─────────────────────────────────────────────────
  const handleQuickAddPerson = async (caseId, caseName) => {
    if (!quickAddPerson.firstName.trim()) return;
    try {
      const saved = await peopleAPI.create({
        firstName: quickAddPerson.firstName,
        lastName: quickAddPerson.lastName,
        caseName,
        caseIds: [caseId],
        status: 'Open', category: 'Person of Interest',
        aliases: [], osintData: [], connections: [], locations: [], attachments: [], custom_fields: {}
      });
      // Link via junction
      await casesAPI.addPerson(caseId, saved.id);
      setQuickAddPerson({ firstName: '', lastName: '' });
      setShowQuickAdd(null);
      refreshAll();
    } catch { alert('Failed to create person'); }
  };

  // ── Entity picker ────────────────────────────────────────────────────────
  const openPicker = (caseId, type) => {
    setPickerMode({ caseId, type });
    setPickerSelected([]);
  };

  const handlePickerConfirm = async (ids) => {
    const { caseId, type } = pickerMode;
    try {
      if (type === 'people') {
        await Promise.all(ids.map(id => casesAPI.addPerson(caseId, id)));
      } else {
        await Promise.all(ids.map(id => casesAPI.addBusiness(caseId, id)));
      }
      refreshAll();
    } catch { alert('Failed to add entities to case'); }
    setPickerMode(null);
    setPickerSelected([]);
  };

  const handleRemovePerson = async (caseId, personId) => {
    try { await casesAPI.removePerson(caseId, personId); refreshAll(); }
    catch { alert('Failed to remove person from case'); }
  };

  const handleRemoveBusiness = async (caseId, businessId) => {
    try { await casesAPI.removeBusiness(caseId, businessId); refreshAll(); }
    catch { alert('Failed to remove business from case'); }
  };

  // People / businesses NOT yet in a specific case (for picker)
  const peopleNotInCase = (caseItem) =>
    people.filter(p => !caseItem.people.some(cp => cp.id === p.id));
  const businessesNotInCase = (caseItem) =>
    businesses.filter(b => !caseItem.businesses.some(cb => cb.id === b.id));

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Case Management</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">{filteredCases.length} cases</p>
        </div>
        <button onClick={() => setShowNewCaseForm(true)}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">
          <Plus className="w-4 h-4 mr-2" />New Case
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input type="text" placeholder="Search cases…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-3 py-2.5 border dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-gray-100" />
      </div>

      {/* New Case Form */}
      {showNewCaseForm && (
        <div className="mb-6 p-5 bg-white dark:bg-slate-800 rounded-lg shadow border dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100">Create New Case</h3>
          <div className="space-y-3">
            <input type="text" placeholder="Case name *" value={newCaseData.case_name}
              onChange={e => setNewCaseData({ ...newCaseData, case_name: e.target.value })}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100" />
            <textarea placeholder="Description" value={newCaseData.description}
              onChange={e => setNewCaseData({ ...newCaseData, description: e.target.value })}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100" rows="2" />
            <div className="flex justify-end space-x-2">
              <button onClick={() => { setShowNewCaseForm(false); setNewCaseData({ case_name: '', description: '', status: 'active' }); }}
                className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
              <button onClick={handleCreateCase} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Create Case</button>
            </div>
          </div>
        </div>
      )}

      {/* Cases List */}
      <div className="space-y-4">
        {filteredCases.map(caseItem => (
          <div key={caseItem.id} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border dark:border-gray-700">

            {/* Case Header */}
            <div className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center space-x-3">
                  <button onClick={() => setExpandedCases(p => ({ ...p, [caseItem.id]: !p[caseItem.id] }))}
                    className="text-gray-500 hover:text-gray-700">
                    {expandedCases[caseItem.id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  <Folder className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">{caseItem.case_name}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    caseItem.status === 'active'  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                    caseItem.status === 'closed'  ? 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300' :
                    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                  }`}>{caseItem.status}</span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-slate-400">
                  <span className="flex items-center space-x-1"><Users className="w-4 h-4" /><span>{caseItem.peopleCount} people</span></span>
                  <span className="flex items-center space-x-1"><Building2 className="w-4 h-4" /><span>{caseItem.businessCount} businesses</span></span>
                  <span className="flex items-center space-x-1"><Clock className="w-4 h-4" /><span>{getTimeAgo(caseItem.lastUpdate)}</span></span>
                  <button onClick={() => { setReportCaseId(caseItem.id); setShowReportGenerator(true); }}
                    className="text-blue-600 hover:text-blue-700" title="Generate Report">
                    <FileText className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditingCase(caseItem.id)} className="text-gray-500 hover:text-gray-700"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDeleteCase(caseItem.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {!expandedCases[caseItem.id] && caseItem.description && (
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-2 ml-8">{caseItem.description}</p>
              )}
            </div>

            {/* Expanded content */}
            {expandedCases[caseItem.id] && (
              <div className="border-t dark:border-gray-700">

                {/* Edit case inline */}
                {editingCase === caseItem.id && (
                  <div className="p-4 bg-gray-50 dark:bg-slate-900 space-y-3 border-b dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Case Name</label>
                        <input type="text" defaultValue={caseItem.case_name}
                          id={`case-name-${caseItem.id}`}
                          className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Status</label>
                        <select defaultValue={caseItem.status} id={`case-status-${caseItem.id}`}
                          className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100">
                          <option value="active">Active</option>
                          <option value="on_hold">On Hold</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Description</label>
                      <textarea defaultValue={caseItem.description || ''} id={`case-desc-${caseItem.id}`}
                        className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100" rows="2" />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button onClick={() => setEditingCase(null)}
                        className="px-3 py-1.5 text-sm text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200">Cancel</button>
                      <button onClick={() => handleUpdateCase(caseItem.id, {
                        case_name: document.getElementById(`case-name-${caseItem.id}`).value,
                        description: document.getElementById(`case-desc-${caseItem.id}`).value,
                        status: document.getElementById(`case-status-${caseItem.id}`).value,
                      })} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">
                        <Save className="w-3.5 h-3.5 mr-1" />Save
                      </button>
                    </div>
                  </div>
                )}

                {/* ── People Section ── */}
                <div className="p-4 border-b dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-800 dark:text-slate-200 flex items-center">
                      <Users className="w-4 h-4 mr-2 text-blue-500" />People ({caseItem.peopleCount})
                    </h4>
                    <div className="flex space-x-2">
                      <button onClick={() => openPicker(caseItem.id, 'people')}
                        className="px-3 py-1 text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-200 flex items-center">
                        <LinkIcon className="w-3.5 h-3.5 mr-1" />Add Existing
                      </button>
                      <button onClick={() => setShowQuickAdd(caseItem.id)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">
                        <Plus className="w-3.5 h-3.5 mr-1" />Quick Add
                      </button>
                    </div>
                  </div>

                  {showQuickAdd === caseItem.id && (
                    <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex space-x-2">
                        <input type="text" placeholder="First Name *" value={quickAddPerson.firstName}
                          onChange={e => setQuickAddPerson(p => ({ ...p, firstName: e.target.value }))}
                          className="flex-1 px-3 py-2 border dark:border-gray-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        <input type="text" placeholder="Last Name" value={quickAddPerson.lastName}
                          onChange={e => setQuickAddPerson(p => ({ ...p, lastName: e.target.value }))}
                          className="flex-1 px-3 py-2 border dark:border-gray-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                        <button onClick={() => handleQuickAddPerson(caseItem.id, caseItem.case_name)}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">Add</button>
                        <button onClick={() => { setShowQuickAdd(null); setQuickAddPerson({ firstName: '', lastName: '' }); }}
                          className="px-3 py-2 bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-slate-300 rounded-lg text-sm">Cancel</button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    {caseItem.people.length === 0 && (
                      <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-3">No people in this case yet</p>
                    )}
                    {caseItem.people.map(person => {
                      const full = people.find(p => p.id === person.id) || person;
                      return (
                        <div key={person.id} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-slate-900 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors">
                          <div className="flex items-center space-x-3">
                            <User className="w-4 h-4 text-gray-400 shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                                {full.first_name} {full.last_name || ''}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-slate-400">
                                {full.category}{full.status ? ` · ${full.status}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button onClick={() => setSelectedPersonForDetail(full)} className="text-xs text-blue-600 hover:text-blue-700">View</button>
                            <button onClick={() => setEditingPerson(full)} className="text-gray-500 hover:text-gray-700"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleRemovePerson(caseItem.id, person.id)}
                              className="text-red-400 hover:text-red-600" title="Remove from case">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ── Businesses Section ── */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-800 dark:text-slate-200 flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-green-500" />Businesses ({caseItem.businessCount})
                    </h4>
                    <button onClick={() => openPicker(caseItem.id, 'businesses')}
                      className="px-3 py-1 text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-200 flex items-center">
                      <LinkIcon className="w-3.5 h-3.5 mr-1" />Add Existing
                    </button>
                  </div>

                  <div className="space-y-1.5">
                    {caseItem.businesses.length === 0 && (
                      <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-3">No businesses in this case yet</p>
                    )}
                    {caseItem.businesses.map(biz => (
                      <div key={biz.id} className="flex items-center justify-between p-2.5 bg-green-50 dark:bg-green-900/10 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors">
                        <div className="flex items-center space-x-3">
                          <Building2 className="w-4 h-4 text-green-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{biz.name}</p>
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                              {biz.industry}{biz.status ? ` · ${biz.status}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button onClick={() => setEditingBusiness(businesses.find(b => b.id === biz.id) || biz)}
                            className="text-gray-500 hover:text-gray-700"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleRemoveBusiness(caseItem.id, biz.id)}
                            className="text-red-400 hover:text-red-600" title="Remove from case">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}
          </div>
        ))}

        {filteredCases.length === 0 && (
          <div className="text-center py-16 text-gray-400 dark:text-slate-500">
            <Folder className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No cases found</p>
          </div>
        )}
      </div>

      {/* Entity Picker Modal */}
      {pickerMode && (() => {
        const caseItem = cases.find(c => c.id === pickerMode.caseId);
        if (!caseItem) return null;
        const isPeople = pickerMode.type === 'people';
        const items = isPeople ? peopleNotInCase(caseItem) : businessesNotInCase(caseItem);
        return (
          <EntityPickerModal
            title={`Add ${isPeople ? 'People' : 'Businesses'} to "${caseItem.case_name}"`}
            items={items}
            selectedIds={pickerSelected}
            onToggle={id => setPickerSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])}
            onConfirm={handlePickerConfirm}
            onCancel={() => { setPickerMode(null); setPickerSelected([]); }}
            renderItem={item => isPeople ? `${item.first_name} ${item.last_name || ''}`.trim() : item.name}
          />
        );
      })()}

      {/* Report Generator */}
      {showReportGenerator && (
        <ReportGenerator caseId={reportCaseId} onClose={() => { setShowReportGenerator(false); setReportCaseId(null); }} />
      )}
    </div>
  );
};

export default CaseManagement;
